import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth';
import MerchantHeader from '../components/MerchantHeader';
import { Store, CheckCircle, RefreshCw, AlertCircle, Package, Zap, ArrowRight, ExternalLink, Shield } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * ShopifyIntegrationPage
 * 
 * UPDATED for Shopify App Store Compliance:
 * - Removed manual store URL input (violates 2.3.1)
 * - Shows connection status and sync controls only
 * - For new connections, merchants should install from Shopify App Store
 * 
 * This page is for the EXTERNAL merchant portal, not the embedded app.
 */
export default function ShopifyIntegrationPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const profileData = await authService.getProfile();
      setUser(profileData.member);
      
      // Load connected stores
      await loadStores();
      
      setLoading(false);
    } catch (error) {
      console.error('Failed to load data:', error);
      navigate('/login');
    }
  };

  const loadStores = async () => {
    try {
      const token = sessionStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/shopify/stores`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStores(data.stores || []);
      }
    } catch (error) {
      console.error('Failed to load stores:', error);
    }
  };

  const handleSyncProducts = async (shopDomain) => {
    setSyncing(true);
    try {
      const token = sessionStorage.getItem('session_token');
      const response = await fetch(`${BACKEND_URL}/api/shopify/sync/products`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          shop_domain: shopDomain,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Synced ${data.synced_count} products from Shopify`);
        await loadStores();
      } else {
        throw new Error('Sync failed');
      }
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('Failed to sync products');
    } finally {
      setSyncing(false);
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    sessionStorage.clear();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: '#0B1120'}}>
        <div className="text-center">
          <svg className="w-12 h-12 text-[#3EF1C7] animate-spin mx-auto mb-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="text-[#9CB0C7]">Loading Shopify integration...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{background: '#0B1120'}}>
      <MerchantHeader user={user} onLogout={handleLogout} />

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Store className="w-8 h-8 text-[#95BF47]" />
            Shopify Integration
          </h1>
          <p className="text-[#9CB0C7]">Manage your connected Shopify stores and sync products</p>
        </div>

        {/* Benefits Banner */}
        <div className="rounded-lg border border-[#95BF47]/30 bg-[#95BF47]/5 p-6 mb-8">
          <h3 className="text-lg font-semibold text-white mb-4">Why Connect Shopify?</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#3EF1C7]/10 flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 text-[#3EF1C7]" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Auto-Sync Products</h4>
                <p className="text-sm text-[#9CB0C7]">Automatically sync your products and inventory</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#7A5CFF]/10 flex items-center justify-center flex-shrink-0">
                <Zap className="w-5 h-5 text-[#7A5CFF]" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Margin Guard</h4>
                <p className="text-sm text-[#9CB0C7]">Monitor fulfillment health and promise metrics</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#8CF78E]/10 flex items-center justify-center flex-shrink-0">
                <RefreshCw className="w-5 h-5 text-[#8CF78E]" />
              </div>
              <div>
                <h4 className="text-white font-medium mb-1">Real-Time Updates</h4>
                <p className="text-sm text-[#9CB0C7]">Keep inventory and pricing always in sync</p>
              </div>
            </div>
          </div>
        </div>

        {stores.length === 0 ? (
          /* No stores connected - Direct to Shopify App Store */
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-12 text-center">
            <div className="w-16 h-16 rounded-full bg-[#95BF47]/10 flex items-center justify-center mx-auto mb-6">
              <Store className="w-8 h-8 text-[#95BF47]" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-3">Connect Your Shopify Store</h3>
            <p className="text-[#9CB0C7] mb-6 max-w-md mx-auto">
              Install Commerce Index from the Shopify App Store to get started with automatic product syncing and intelligent deal management.
            </p>
            
            {/* Install from Shopify App Store - Compliant Method */}
            <div className="max-w-md mx-auto space-y-4">
              <a
                href="https://apps.shopify.com/commerce-index"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full px-6 py-3 rounded-lg bg-[#95BF47] text-white font-semibold hover:bg-[#95BF47]/90 flex items-center justify-center gap-2"
                data-testid="install-from-shopify-button"
              >
                <Store className="w-5 h-5" />
                Install from Shopify App Store
                <ExternalLink className="w-4 h-4" />
              </a>
              
              <div className="flex items-center gap-2 justify-center text-sm text-[#9CB0C7]">
                <Shield className="w-4 h-4 text-[#3EF1C7]" />
                <span>Secure OAuth connection via Shopify</span>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-[#22304A]">
              <p className="text-sm text-[#9CB0C7]">
                Need help? <a href="mailto:support@commerceindex.com" className="text-[#3EF1C7] hover:underline">Contact support</a>
              </p>
            </div>
          </div>
        ) : (
          /* Connected stores */
          <div className="space-y-6">
            {stores.map((store) => (
              <div
                key={store.shop_domain}
                className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-lg bg-[#95BF47]/10 flex items-center justify-center">
                        <Store className="w-6 h-6 text-[#95BF47]" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">{store.shop_domain}</h3>
                        <p className="text-sm text-[#9CB0C7]">
                          Connected {new Date(store.installed_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-[#8CF78E]" />
                      <span className="text-sm text-[#8CF78E]">Active</span>
                    </div>
                  </div>

                  {store.last_sync && (
                    <div className="flex items-center gap-2 text-sm text-[#9CB0C7] mb-4">
                      <RefreshCw className="w-4 h-4" />
                      Last synced: {new Date(store.last_sync).toLocaleString()}
                    </div>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={() => handleSyncProducts(store.shop_domain)}
                      disabled={syncing}
                      className="px-4 py-2 rounded-lg bg-[#7A5CFF] text-white hover:bg-[#7A5CFF]/90 disabled:opacity-50 flex items-center gap-2"
                      data-testid="sync-products-button"
                    >
                      <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                      {syncing ? 'Syncing...' : 'Sync Products'}
                    </button>
                    <button
                      onClick={() => navigate('/dashboard/products')}
                      className="px-4 py-2 rounded-lg border border-[#22304A] text-[#9CB0C7] hover:bg-[#1A2437] flex items-center gap-2"
                    >
                      View Products
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Info about adding stores */}
            <div className="rounded-lg border border-dashed border-[#22304A] bg-[#0E1426]/40 p-6 text-center">
              <p className="text-[#9CB0C7] mb-4">Want to connect another store?</p>
              <a
                href="https://apps.shopify.com/commerce-index"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#95BF47] text-white hover:bg-[#95BF47]/90"
              >
                <Store className="w-4 h-4" />
                Install on Another Store
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
